import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Html5Qrcode } from 'html5-qrcode';
import { makeStyles } from '@mui/styles';
import { api } from 'common';
import moment from 'moment/min/moment-with-locales';
import AlertDialog from '../components/AlertDialog';

const QR_REGION_ID = 'batch-lookup-qr-reader';

const useStyles = makeStyles(() => ({
    container: {
        backgroundColor: 'var(--bg-main)',
        minHeight: '100vh',
        padding: '24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
    },
    card: {
        borderRadius: '20px',
        backgroundColor: 'var(--bg-card)',
        border: 'var(--card-border)',
        boxShadow: 'var(--shadow)',
        width: '100%',
        maxWidth: '800px',
        padding: '30px',
        marginTop: '20px'
    },
    textField: {
        marginBottom: '10px',
        "& label.Mui-focused": {
            color: '#E9AE31',
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: '#E9AE31',
            },
            "& input": {
                color: 'var(--text-main)'
            }
        },
        "& .MuiInputLabel-root": {
            color: 'var(--text-secondary)'
        }
    },
    resultCard: {
        marginTop: '20px',
        padding: '20px',
        borderRadius: '14px',
        border: '1px solid rgba(233, 174, 49, 0.35)',
        backgroundColor: 'rgba(233, 174, 49, 0.06)'
    },
    resultRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
    },
    scanBtn: {
        borderRadius: '19px',
        minHeight: '46px',
        fontWeight: 'bold',
        marginBottom: '15px'
    },
    cameraBox: {
        width: '100%',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '15px',
        border: '1px solid rgba(233, 174, 49, 0.35)'
    }
}));

// Admin tool: scan (via any USB/Bluetooth barcode-scanner "keyboard wedge" device) or paste a
// printed sticker's QR content / serial number here and it looks up that serial's batch info -
// the input just needs to stay focused, the scanner does the rest (types the decoded text + Enter).
export default function BatchLookup() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const dispatch = useDispatch();
    const inputRef = useRef(null);

    const { verifySerial } = api;

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [alertState, setAlertState] = useState({ open: false, msg: '' });
    const [scanning, setScanning] = useState(false);
    const html5QrRef = useRef(null);
    const scanHandledRef = useRef(false);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Starts/stops the in-browser camera scanner whenever `scanning` toggles. Runs as an effect
    // (rather than inline in the button handler) because Html5Qrcode needs the #QR_REGION_ID div
    // to already exist in the DOM, which only happens once React has rendered it.
    useEffect(() => {
        if (!scanning) return undefined;

        // Guards against Html5Qrcode firing its success callback more than once for the same
        // sticker before we manage to stop the scanner (decoding runs on a frame-by-frame loop).
        scanHandledRef.current = false;

        const qr = new Html5Qrcode(QR_REGION_ID);
        html5QrRef.current = qr;

        qr.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: 250 },
            (decodedText) => {
                if (scanHandledRef.current) return;
                scanHandledRef.current = true;
                handleLookup(decodedText);
                // One scan and done - camera closes itself instead of continuing to scan the
                // same sticker (or the next one) automatically.
                setScanning(false);
            },
            () => { /* per-frame "no QR found" - expected on most frames, ignore */ }
        ).catch((err) => {
            setScanning(false);
            setAlertState({ open: true, msg: `Unable to access camera: ${err?.message || err}` });
        });

        return () => {
            const activeQr = html5QrRef.current;
            html5QrRef.current = null;
            if (activeQr) {
                activeQr.stop().then(() => activeQr.clear()).catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanning]);

    const handleAlertClose = (e) => {
        e.preventDefault();
        setAlertState({ open: false, msg: '' });
        if (!scanning) inputRef.current?.focus();
    };

    // `rawValue` is passed by the camera scanner (the decoded QR text); manual/scanner-gun
    // entry via the text field omits it and falls back to the current input state.
    const handleLookup = async (rawValue) => {
        const value = (rawValue !== undefined ? rawValue : input).trim();
        if (!value) return;

        setLoading(true);
        const res = await dispatch(verifySerial(value));
        setLoading(false);
        setInput('');
        if (!scanning) inputRef.current?.focus();

        if (res && res.valid) {
            const entry = {
                serialNumber: res.serialNumber,
                batchNumber: res.data?.batchNumber || '—',
                batchSequence: res.data?.batchSequence,
                batchTotal: res.data?.batchTotal,
                createdAt: res.data?.createdAt,
                scannedAt: Date.now()
            };
            setResult(entry);
            setHistory((prev) => [entry, ...prev].slice(0, 25));
        } else {
            setResult(null);
            setAlertState({ open: true, msg: t('batch_lookup_not_found') });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleLookup();
        }
    };

    return (
        <div className={classes.container}>
            <Card className={classes.card}>
                <CardContent style={{ padding: '10px' }}>
                    <Typography
                        variant="h5"
                        style={{
                            marginBottom: '25px',
                            textAlign: isRTL === 'rtl' ? 'right' : 'left',
                            fontWeight: 'bold',
                            fontFamily: 'Inter, sans-serif',
                            color: 'var(--text-main)'
                        }}
                    >
                        {t('batch_lookup')}
                    </Typography>

                    <Button
                        className={classes.scanBtn}
                        variant={scanning ? 'outlined' : 'contained'}
                        fullWidth
                        startIcon={scanning ? <StopCircleIcon /> : <CameraAltIcon />}
                        onClick={() => setScanning((prev) => !prev)}
                        style={scanning
                            ? { borderColor: '#E9AE31', color: '#E9AE31' }
                            : { backgroundColor: '#E9AE31', color: '#FFFFFF' }}
                    >
                        {scanning ? t('stop_camera_scan') : t('start_camera_scan')}
                    </Button>

                    {scanning && <div id={QR_REGION_ID} className={classes.cameraBox} />}

                    <TextField
                        inputRef={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        autoFocus
                        disabled={loading}
                        className={classes.textField}
                        placeholder={t('batch_lookup_placeholder')}
                    />

                    {result && (
                        <div className={classes.resultCard}>
                            <div className={classes.resultRow}>
                                <Typography style={{ color: 'var(--text-secondary)' }}>Serial Number</Typography>
                                <Typography style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>{result.serialNumber}</Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography style={{ color: 'var(--text-secondary)' }}>{t('batch_number')}</Typography>
                                <Typography style={{ color: '#E9AE31', fontWeight: 'bold' }}>
                                    {result.batchNumber}
                                    {result.batchSequence && result.batchTotal ? ` (${String(result.batchSequence).padStart(String(result.batchTotal).length, '0')}/${result.batchTotal})` : ''}
                                </Typography>
                            </div>
                            <div className={classes.resultRow} style={{ borderBottom: 'none' }}>
                                <Typography style={{ color: 'var(--text-secondary)' }}>Generated</Typography>
                                <Typography style={{ color: 'var(--text-main)' }}>{result.createdAt ? moment(result.createdAt).format('lll') : '—'}</Typography>
                            </div>
                        </div>
                    )}

                    {history.length > 0 && (
                        <TableContainer style={{ marginTop: '30px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ color: 'var(--text-secondary)' }}>Serial Number</TableCell>
                                        <TableCell style={{ color: 'var(--text-secondary)' }}>Batch</TableCell>
                                        <TableCell style={{ color: 'var(--text-secondary)' }}>Scanned</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((entry, i) => (
                                        <TableRow key={i}>
                                            <TableCell style={{ color: 'var(--text-main)' }}>{entry.serialNumber}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={entry.batchSequence && entry.batchTotal ? `${entry.batchNumber} · ${String(entry.batchSequence).padStart(String(entry.batchTotal).length, '0')}/${entry.batchTotal}` : entry.batchNumber}
                                                    style={{ backgroundColor: 'rgba(233, 174, 49, 0.15)', color: '#E9AE31' }}
                                                />
                                            </TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)' }}>{moment(entry.scannedAt).format('LT')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={alertState.open} onClose={handleAlertClose}>
                {alertState.msg}
            </AlertDialog>
        </div>
    );
}
